import React, { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuthContext } from '@/contexts/AuthContext';
import { getStoredAffiliateRef } from '@/lib/marketing';

/**
 * The Charter Sale. A deadline offer gets its own page rather than a row on
 * /plans, because what sells it — the calendar behind it, the price against
 * renewing normally, and when it stops — needs room and would be noise on the
 * standing pricing page.
 *
 * This page is a LETTER, not a landing page. The body is Joe's own first person
 * and the people reading it have had several personal emails from him this
 * month; a glossy sales page would read as less trustworthy than plain text.
 * So the design does three jobs and nothing else: it puts a Hey Joe masthead on
 * the letter, it gives the deadline its own presence, and it makes the two
 * offers legible as a choice. No hero image, no feature grid, no testimonials.
 *
 * DATES AND PRICES ARE NEVER TYPED INTO THIS FILE. They render from
 * /api/charter/status, which reads the same config the server enforces the sale
 * window against and the same seat ledger the 50-seat cap is enforced against. A
 * date written in prose here could tell someone the sale is open on a day the
 * server will refuse them, and a seat count could promise a seat that is gone.
 */

type CharterTier = {
  tier: string;
  name: string;
  priceCents: number;
  termMonths: number;
  seatCap: number | null;
  seatsLeft: number | null;
  available: boolean;
  released?: boolean;   // a gated tier stays hidden until the tier it backs up sells out
  soldOut?: boolean;
};
type CharterStatus = {
  open: boolean;
  opensAt: string;
  closesAt: string;
  renewsAtCents: number;
  refundDays: number;
  tiers: CharterTier[];
};

/**
 * The third way in. David Bianchi's Master Incubator bundles a Charter membership
 * with a two month intensive, so it belongs on this page — but it is a creator
 * PRODUCT, not a charter tier, and it is deliberately not sold from here.
 *
 * Two reasons it links out rather than checking out inline. Its selling window
 * OUTLIVES the sale: the cohort's first session is days after Charter closes, and
 * every offer on this page goes dead the moment charterSaleOpen() flips. And what
 * justifies its price — the dates, the size of the room, the testimonials — is
 * exactly what this page deliberately does not carry.
 *
 * It renders from the product's own public endpoint, never from constants typed
 * here, for the same reason the tiers do: a seat count written into this file
 * could promise a seat that is gone. That endpoint serves PUBLISHED products only,
 * so an unpublished or retired Incubator makes this card disappear on its own
 * instead of pointing at a page nobody can open. Sold out hides it too — this card
 * exists to route people to something they can actually buy.
 *
 * The id rather than the slug is not a style choice: /api/creator/products/:id/public
 * parses its param as an ObjectId and 404s on anything else.
 */
const INCUBATOR_ID = '6a8b53032e3d0db8f66ff7f1';
type IncubatorCard = {
  title: string;
  price: number;
  instructor?: string;
  formatLabel?: string;
  cohortSeatsRemaining?: number | null;
  cohortSoldOut?: boolean;
};

const money = (cents: number) => `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;

// "Monday, August 24" — the weekday comes from the date rather than being written
// alongside it, so the two can never disagree.
const dayLine = (iso: string) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'America/Los_Angeles' });
};
const dayTimeLine = (iso: string) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const day = d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'America/Los_Angeles' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Los_Angeles' });
  return `${day} at ${time} PT`;
};

const INCLUDED = [
  'Free access to 2 to 4 featured classes every month',
  'All of our audition and self-tape editing tools',
  'The Book a Reader platform',
  'Up to 10 videos on your casting profile',
  'Member pricing on workshops and courses in the marketplace',
  'Every class replay in your library, permanently',
];

const DARK_ON_YELLOW = 'oklch(0.18 0.005 70)';

/** The marker swipe — a yellow block behind a word — is the brand's device for
 *  emphasising TYPE, and it is what the price gets. The starburst was tried here
 *  first and failed: its solid core is narrower than the number, so the outer
 *  glyphs fell onto black and the one figure the letter is about stopped being
 *  readable. A highlight has to be a rectangle to hold a word. */
const Swipe: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span
    style={{
      display: 'inline-block',
      padding: '0.02em 0.16em',
      background: 'var(--hj-yellow)',
      color: DARK_ON_YELLOW,
      clipPath: 'polygon(2% 8%, 98% 0%, 100% 92%, 0% 100%)',
      transform: 'rotate(-1.5deg)',
    }}
  >
    {children}
  </span>
);

/* The brand starburst (public/illustrations/why-starburst-large.png) was tried
   here three ways and is deliberately not on this page.
     - Behind the price: its solid core is narrower than the number, so the outer
       glyphs fell onto black and the price stopped being readable.
     - As a letterhead mark above the eyebrow: at any restrained size it reads as
       a sparkle icon, decoration with no job, on a page that is a letter.
     - Bled off the right of the deadline band: it collides with the yellow swipe
       directly above it and the crop reads as a rendering fault.
   The structural reason is worth writing down so nobody re-litigates it: this
   page is dark, so a large yellow shape can never sit behind type (light text on
   yellow has no contrast), and the one dark-on-yellow moment is already the
   swipe. That leaves only empty space, where it is pure ornament. The arrow
   below stays because it does a job. */

/** The brand's hand-drawn arrow, doing what someone scribbling in a margin does:
 *  pointing from the end of the letter down into the two offers.
 *
 *  The asset is black ink, drawn for the cream marketing site. On this page it
 *  would be invisible, so it is inverted to white and dropped to the weight of
 *  a pencil line. Never place this file on dark without the invert. */
const MarginArrow: React.FC = () => (
  <img
    src="/illustrations/curve-arrow-down-1.png" alt="" aria-hidden
    style={{
      display: 'block', width: 152, height: 'auto',
      filter: 'invert(1)', opacity: 0.5, pointerEvents: 'none',
    }}
  />
);

/** One dated row in the deadline band, set like a line on a docket: label in a
 *  fixed column, date beside it, so the two rows share a baseline instead of
 *  floating. `now` is the date currently acting on the reader — before the sale
 *  it is the opening, once it is running it is the close. */
const DateRow: React.FC<{ label: string; value: string; now?: boolean; first?: boolean }> = ({ label, value, now, first }) => (
  <div
    style={{
      display: 'flex', alignItems: 'baseline', gap: 16,
      padding: first ? '0 0 9px' : '9px 0 0',
      borderTop: first ? undefined : '1px solid var(--hj-hairline-soft)',
    }}
  >
    <span
      className="mono-label"
      style={{ fontSize: 10.5, letterSpacing: '0.1em', color: 'var(--hj-ink-3)', width: 58, flexShrink: 0 }}
    >
      {label}
    </span>
    <span
      className="font-hj-display"
      style={{
        fontSize: now ? 19.5 : 17, lineHeight: 1.25, letterSpacing: '-0.015em',
        color: 'var(--hj-ink)', fontWeight: now ? 700 : 500,
        // stops "…11:59 PM / PT" orphaning the timezone on a phone
        textWrap: 'balance',
      }}
    >
      {value}
    </span>
  </div>
);

const CharterPage: React.FC = () => {
  const { toast } = useToast();
  const { isAuthenticated } = useAuthContext();
  const [status, setStatus] = useState<CharterStatus | null>(null);
  // Closed is not the same as not-yet-open. Both used to fall through the same
  // !status.open branch, so once the sale ended the page advertised "Opens
  // August 24" — a date already in the past — beside a dead button.
  const closed = !!status && !status.open && Date.now() >= new Date(status.closesAt).getTime();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [incubator, setIncubator] = useState<IncubatorCard | null>(null);

  // Failures are silent on purpose: a 404 is the ordinary case while the product
  // is unpublished, and the card simply does not appear. Nothing else on this
  // page depends on it, so it must never block the sale rendering.
  useEffect(() => {
    let cancelled = false;
    fetch(`/api/creator/products/${INCUBATOR_ID}/public`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!cancelled && d?.product?.title) setIncubator(d.product as IncubatorCard); })
      .catch(() => { /* card stays hidden */ });
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/charter/status');
      if (res.ok) setStatus(await res.json());
    } catch { /* falls through to the quiet unavailable state */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const buy = async (tier: string) => {
    if (!isAuthenticated) {
      // Back here after signing in, not the dashboard — that's where a deadline
      // offer loses people.
      window.location.href = `/auth?returnTo=${encodeURIComponent('/charter')}`;
      return;
    }
    setBusy(tier);
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const res = await fetch('/api/subscription/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: 'include',
        // Whoever's affiliate link brought them here. Captured on landing (see
        // lib/marketing) rather than read off the URL, because the sign-in round
        // trip above drops the query string. The server ignores a ref it can't
        // resolve, so a stale or bogus one never affects what they pay.
        body: JSON.stringify({ tier, returnTo: '/charter', ...(getStoredAffiliateRef() ? { ref: getStoredAffiliateRef() } : {}) }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.checkoutUrl) { window.location.href = data.checkoutUrl; return; }
      throw new Error(data.error || 'Could not start checkout. Please try again.');
    } catch (e: any) {
      toast({ title: 'Could not start checkout', description: e.message, variant: 'destructive' });
      setBusy(null);
      load();   // the likeliest cause is the last seat going while they decided
    }
  };

  const oneYr = status?.tiers.find(t => t.tier === 'charter_1yr');
  const twoYr = status?.tiers.find(t => t.tier === 'charter_2yr');
  const threeYr = status?.tiers.find(t => t.tier === 'charter_3yr');
  // Order is deliberate: 1yr, 2yr, 3yr. The three-year stays on the page after it
  // sells out, struck out and marked, because a cap that quietly disappears reads
  // as a price change. Left in place it is proof the fifty were real, and it puts
  // the sold-out marker at the top of the ladder where it does the most work.
  // The two-year only appears once the three-year is actually gone.
  const visibleTiers = [oneYr, twoYr?.released ? twoYr : null, threeYr].filter(Boolean) as CharterTier[];
  const renews = status ? money(status.renewsAtCents) : '';
  // The comparison, computed from the live prices rather than written down.
  const threeYearNormal = status && oneYr ? oneYr.priceCents + status.renewsAtCents * 2 : 0;
  const annualSaving = status && oneYr ? status.renewsAtCents - oneYr.priceCents : 0;

  if (loading) {
    return (
      <main style={{ background: 'var(--hj-bg)', minHeight: 'calc(100vh - 68px)', display: 'grid', placeItems: 'center' }}>
        <Loader2 className="animate-spin" size={22} style={{ color: 'var(--hj-ink-3)' }} />
      </main>
    );
  }

  const para: React.CSSProperties = { margin: '0 0 16px', fontSize: 16.5, lineHeight: 1.6, color: 'var(--hj-ink)' };
  const disclosure: React.CSSProperties = {
    margin: 0, padding: '13px 0', borderTop: '1px solid var(--hj-hairline-soft)',
    fontSize: 15.5, lineHeight: 1.6, color: 'var(--hj-ink)',
  };

  return (
    <main style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)', minHeight: 'calc(100vh - 68px)' }}>
      <div className="mx-auto" style={{ maxWidth: 760, padding: '0 20px 96px' }}>

        {/* Masthead. A letter still has letterhead — a rule, a name and the
            marker swipe are enough to say Hey Joe without staging a hero. */}
        <header style={{ padding: '40px 0 0' }}>
          {/* Letterhead. A rule rather than an illustration: it says Hey Joe
              without putting a marketing sparkle on top of a personal letter. */}
          <div
            aria-hidden
            style={{ height: 5, width: 74, background: 'var(--hj-yellow)', borderRadius: 999, marginBottom: 18 }}
          />
          {oneYr && (
            <div
              className="mono-label"
              style={{ fontSize: 11, letterSpacing: '0.12em', color: 'var(--hj-ink-2)', marginBottom: 12 }}
            >
              Hey Joe Plus · Charter Membership
            </div>
          )}
          <h1
            className="font-hj-display m-0"
            style={{ fontSize: 'clamp(31px, 4.6vw, 50px)', lineHeight: 1.14, letterSpacing: '-0.025em' }}
          >
            {closed
              ? <>Charter Membership is <Swipe>closed</Swipe><span style={{ marginLeft: '-0.08em' }}>.</span></>
              : oneYr
                ? <>A year of Hey Joe Plus for <Swipe>{money(oneYr.priceCents)}</Swipe><span style={{ marginLeft: '-0.08em' }}>.</span></>
                : 'Charter Membership'}
          </h1>

          {/* The deadline. It is the reason to act, so it gets its own band
              instead of a clause in a sentence. Before the sale opens the
              opening date leads; once it is running the closing date does. */}
          {status && closed && (
            <section
              aria-label="Charter is closed"
              style={{
                marginTop: 22,
                background: 'var(--hj-beige)',
                borderLeft: '4px solid var(--hj-yellow)',
                borderRadius: 'var(--hj-r-sm)',
                padding: '22px 24px',
              }}
            >
              <p style={{ margin: '0 0 10px', fontSize: 19, fontWeight: 800, lineHeight: 1.4, color: 'var(--hj-beige-ink)' }}>
                Charter memberships are now closed. Thank you to everyone who participated.
              </p>
              <p style={{ margin: 0, fontSize: 16, lineHeight: 1.55, color: 'var(--hj-beige-ink)' }}>
                If you're reaching this page and you were interested in Charter but had any issues
                checking out or missed the window,{' '}
                <a
                  href="mailto:hello@heyjoe.io?subject=Charter%20membership"
                  style={{ color: 'var(--hj-beige-ink)', fontWeight: 800, textDecoration: 'underline' }}
                >
                  email us
                </a>{' '}
                and we'll be happy to look into it for you.
              </p>
            </section>
          )}

          {status && !closed && (
            <section
              aria-label="Sale dates"
              style={{
                marginTop: 22,
                background: 'var(--hj-surface-1)',
                border: '1px solid var(--hj-hairline)',
                borderLeft: '3px solid var(--hj-yellow)',
                borderRadius: 'var(--hj-r-sm)',
                padding: '15px 20px',
              }}
            >
              <div
                className="mono-label"
                style={{ fontSize: 10.5, letterSpacing: '0.12em', color: 'var(--hj-ink-3)', marginBottom: 11 }}
              >
                One week only
              </div>
              <div>
                {!status.open && <DateRow first label="Opens" value={dayLine(status.opensAt)} now />}
                <DateRow first={status.open} label="Closes" value={dayTimeLine(status.closesAt)} now={status.open} />
              </div>
            </section>
          )}
        </header>

        {!status && (
          <p style={{ marginTop: 30, color: 'var(--hj-ink-2)' }}>
            This isn’t available right now. Please check back shortly.
          </p>
        )}

        {status && (
          <>
            <section style={{ marginTop: 32 }}>
              <p style={para}>
                We're opening <strong>Hey Joe Plus Charter Memberships</strong> from now until 8/31.
              </p>
              <p style={para}>
                In July, we opened the Hey Joe Plus marketplace, bringing working casting directors,
                coaches and filmmakers onto the platform to teach.
              </p>
              <p style={para}>
                In the first month alone, we hosted David Bianchi on self-tapes, Francene Selkirk on
                taking control of your career, Jen DiBella on choosing workshops, and a
                four-and-a-half-hour commercial audition workshop with casting director Hunter McHugh
                and two working agents.
              </p>
              <p style={para}>Every session is recorded and added to the member library.</p>
              <p style={{ ...para, marginBottom: 0 }}>
                And we're just getting started. September and October are already filling up, and
                we're booking the rest of the year now with more casting directors, working actors,
                coaches and people from outside the industry who know how to help talented people
                perform under pressure.
              </p>
            </section>

            <div style={{ marginTop: 6, marginBottom: -14, display: 'flex', justifyContent: 'flex-end', paddingRight: 34 }}>
              <MarginArrow />
            </div>

            {/* The two offers. Same paper, so neither looks like the trick one;
                the three-year carries a yellow rail and a seat count because it
                is the bigger commitment and the finite one. */}
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginTop: 10 }}>
              {visibleTiers.map((t) => {
                const tier = t as CharterTier;
                const isThree = tier.tier === 'charter_3yr';
                const isTwo = tier.tier === 'charter_2yr';
                const soldOut = tier.seatsLeft !== null && tier.seatsLeft <= 0;
                const live = tier.available && !soldOut;
                return (
                  <div
                    key={tier.tier}
                    data-paper="beige"
                    style={{
                      background: 'var(--hj-beige)',
                      border: '1px solid var(--hj-beige-line)',
                      // The yellow rail marks whichever tier is the finite one. Once
                      // the three-year is gone it hands that role to the two-year.
                      borderTop: (isThree && !soldOut) || (isTwo && threeYr?.soldOut)
                        ? '4px solid var(--hj-yellow)' : '1px solid var(--hj-beige-line)',
                      color: 'var(--hj-beige-ink)',
                      borderRadius: 'var(--hj-r-md)',
                      padding: (isThree && !soldOut) || (isTwo && threeYr?.soldOut) ? '21px 24px 24px' : '24px',
                      opacity: soldOut ? 0.62 : 1,
                      display: 'flex', flexDirection: 'column', gap: 12,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', minHeight: 22 }}>
                      <span className="mono-label" style={{ fontSize: 11, letterSpacing: '0.12em', color: 'var(--hj-beige-ink-2)' }}>
                        {tier.termMonths} months
                      </span>
                      {tier.seatCap !== null && (
                        <span
                          className="mono-label"
                          style={{
                            fontSize: 10.5, letterSpacing: '0.08em', padding: '4px 9px', borderRadius: 999,
                            background: soldOut ? 'var(--hj-beige-deep)' : 'var(--hj-beige-ink)',
                            color: soldOut ? 'var(--hj-beige-ink-2)' : 'var(--hj-beige)',
                            fontWeight: 700,
                          }}
                        >
                          {soldOut
                            ? 'Sold out'
                            : tier.seatsLeft === tier.seatCap
                              ? `${tier.seatCap} seats`
                              : `${tier.seatsLeft} of ${tier.seatCap} left`}
                        </span>
                      )}
                    </div>

                    <div
                      className="font-hj-display"
                      style={{
                        fontSize: 46, lineHeight: 1, letterSpacing: '-0.035em', color: 'var(--hj-beige-ink)',
                        textDecoration: soldOut ? 'line-through' : 'none',
                      }}
                    >
                      {money(tier.priceCents)}
                    </div>

                    <p className="m-0" style={{ fontSize: 14.5, lineHeight: 1.55, color: 'var(--hj-beige-ink)' }}>
                      {isThree ? (
                        <>Three years. Normally {money(threeYearNormal)}. Renews at {renews} a year after that.{' '}
                          {tier.seatCap !== null && (
                            <strong>{soldOut ? `All ${tier.seatCap} were claimed.` : `Only ${tier.seatCap} of these exist${tier.seatsLeft !== tier.seatCap ? ` — ${tier.seatsLeft} left` : ''}.`}</strong>
                          )}
                        </>
                      ) : isTwo ? (
                        <>Two years. Renews at {renews} a year after that.{' '}
                          <strong>Opened when the three-year sold out.</strong>
                        </>
                      ) : (
                        <>That is {money(annualSaving)} off the annual rate. Renews at {renews} a year after that.</>
                      )}
                    </p>
                    <div style={{ flex: 1, minHeight: 4 }} />
                    <button
                      type="button"
                      disabled={!live || busy === tier.tier}
                      onClick={() => buy(tier.tier)}
                      style={{
                        padding: '13px 22px', borderRadius: 999, border: 'none',
                        background: live ? 'var(--hj-yellow)' : 'var(--hj-beige-deep)',
                        color: live ? DARK_ON_YELLOW : 'var(--hj-beige-ink-2)',
                        fontSize: 15.5, fontWeight: 700, fontFamily: 'inherit', minHeight: 48,
                        cursor: live ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {busy === tier.tier ? 'Taking you to checkout…'
                        : soldOut ? 'Sold out'
                          : closed ? 'Charter is closed'
                            : !status.open ? `Opens ${dayLine(status.opensAt).replace(/,.*/, '')}`
                            : `Join for ${money(tier.priceCents)}`}
                    </button>
                  </div>
                );
              })}
            </div>

            <section style={{ marginTop: 40 }}>
              <h2 className="font-hj-display m-0" style={{ fontSize: 24, letterSpacing: '-0.02em' }}>What membership includes</h2>
              <ul style={{ listStyle: 'none', padding: 0, margin: '16px 0 0', display: 'grid', gap: 9 }}>
                {INCLUDED.map(line => (
                  <li key={line} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 16, lineHeight: 1.5, color: 'var(--hj-ink)' }}>
                    <span aria-hidden style={{ flexShrink: 0, marginTop: 9, width: 5, height: 5, borderRadius: '50%', background: 'var(--hj-yellow)' }} />
                    {line}
                  </li>
                ))}
              </ul>
            </section>

            <section
              data-paper="beige"
              style={{
                marginTop: 30, padding: '22px 24px', borderRadius: 'var(--hj-r-md)',
                background: 'var(--hj-beige)', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)',
              }}
            >
              <h2 className="font-hj-display m-0" style={{ fontSize: 21, letterSpacing: '-0.02em', color: 'var(--hj-beige-ink)' }}>
                Plus, for Charter Members only
              </h2>
              <p className="m-0" style={{ marginTop: 10, fontSize: 15.5, lineHeight: 1.55, color: 'var(--hj-beige-ink)' }}>
                A private Charter Member channel in our Discord that I post in, where you can
                connect with each other, ask for feedback on a tape, or just say hi.
              </p>
            </section>

            {incubator && !incubator.cohortSoldOut && (
              <section style={{ marginTop: 30 }}>
                <h2 className="font-hj-display m-0" style={{ fontSize: 24, letterSpacing: '-0.02em' }}>There is a third way in</h2>
                <div
                  data-paper="beige"
                  style={{
                    marginTop: 14, padding: '21px 24px 24px', borderRadius: 'var(--hj-r-md)',
                    background: 'var(--hj-beige)', border: '1px solid var(--hj-beige-line)',
                    borderTop: '4px solid var(--hj-yellow)', color: 'var(--hj-beige-ink)',
                    display: 'flex', flexDirection: 'column', gap: 12,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', minHeight: 22 }}>
                    <span className="mono-label" style={{ fontSize: 11, letterSpacing: '0.12em', color: 'var(--hj-beige-ink-2)' }}>
                      {incubator.formatLabel || 'Intensive'}
                    </span>
                    {typeof incubator.cohortSeatsRemaining === 'number' && (
                      <span
                        className="mono-label"
                        style={{
                          fontSize: 10.5, letterSpacing: '0.08em', padding: '4px 9px', borderRadius: 999,
                          background: 'var(--hj-beige-ink)', color: 'var(--hj-beige)',
                        }}
                      >
                        {incubator.cohortSeatsRemaining} seats left
                      </span>
                    )}
                  </div>
                  <div
                    className="font-hj-display"
                    style={{ fontSize: 26, lineHeight: 1.08, letterSpacing: '-0.03em', color: 'var(--hj-beige-ink)' }}
                  >
                    {incubator.title}
                  </div>
                  <p className="m-0" style={{ fontSize: 15.5, lineHeight: 1.55, color: 'var(--hj-beige-ink)' }}>
                    An immersive two month intensive for serious actors committed to mastering performance,
                    light, picture and sound to level up their self tape auditions to an elite level.
                    Personally coached by veteran actor / producer and MYST creator David Bianchi.
                  </p>
                  <p className="m-0" style={{ fontSize: 15.5, lineHeight: 1.55, color: 'var(--hj-beige-ink)', fontWeight: 700 }}>
                    Includes the entire MYST class and one year of charter membership.
                  </p>
                  <a
                    href={`/course/${INCUBATOR_ID}`}
                    style={{
                      alignSelf: 'flex-start', padding: '13px 22px', borderRadius: 999,
                      background: 'var(--hj-yellow)', color: DARK_ON_YELLOW,
                      fontSize: 15.5, fontWeight: 700, textDecoration: 'none', minHeight: 48,
                      display: 'inline-flex', alignItems: 'center',
                    }}
                  >
                    See the Incubator
                  </a>
                </div>
              </section>
            )}

            {/* The mandatory disclosures, in plain sight rather than a footnote.
                Stated badly, these are what produce chargebacks. Hairlines let
                someone find the one they came for without any of it shrinking. */}
            <section style={{ marginTop: 34 }}>
              <h2 className="font-hj-display m-0" style={{ fontSize: 24, letterSpacing: '-0.02em' }}>Details</h2>
              <div style={{ marginTop: 14 }}>
                <p style={disclosure}>
                  If you are already a member, we will add the time you have already paid for onto
                  your Charter term. You will not lose a day and you will not be billed twice.
                </p>
                <p style={disclosure}>
                  {status.refundDays} day refund. If you change your mind, email me and we will
                  refund you.
                </p>
                <p style={{ ...disclosure, borderBottom: '1px solid var(--hj-hairline-soft)' }}>
                  Closes {dayTimeLine(status.closesAt)}.
                </p>
              </div>
            </section>
          </>
        )}
      </div>
    </main>
  );
};

export default CharterPage;
